<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="errorMessage" />

        <v-card-title class="headline">
          {{ $t('@reducePremiumForm/changePremiumTitle') }}
        </v-card-title>

        <v-card-text>
          <section>
            <v-select
              :items="variationsWithoutCurrent"
              :disabled="!variations.length"
              v-model="selectedCoverage"
              item-text="covered_amount"
              item-value="covered_amount"
              :label="$t('@reducePremiumForm/premiumSelectLabel')"
            ></v-select>

            <div class="v-table__overflow">
              <table class="v-datatable v-table theme--light">
                <thead>
                  <tr>
                    <th></th>
                    <th>{{ $t('@reducePremiumForm/currentValueColumnHeader') }}</th>
                    <th>{{ $t('@reducePremiumForm/newValueColumnHeader') }}</th>
                    <th>
                      {{
                        $t('@reducePremiumForm/differenceBetwencurrentAndUpdatedValuesColumnHeader')
                      }}
                    </th>
                  </tr>
                </thead>

                <tbody v-if="selectedVariation">
                  <tr>
                    <td class="column-title">
                      {{ $t('@reducePremiumForm/coveredAmountRowPrefixText') }}
                    </td>
                    <td>{{ contract.productVariation.coveredAmount }} kr</td>
                    <td>{{ selectedVariation.coveredAmount }} kr</td>
                    <td v-if="diffPremium >= 0">
                      {{
                        $t(
                          '@reducePremiumForm/differenceBetweenCurrentAndUpdatedValuesRowTextLess',
                          {
                            diffValue: diffPremium,
                          },
                        )
                      }}
                    </td>
                    <td v-else>
                      {{
                        $t(
                          '@reducePremiumForm/differenceBetweenCurrentAndUpdatedValuesRowTextMore',
                          {
                            diffValue: Math.abs(diffPremium),
                          },
                        )
                      }}
                    </td>
                  </tr>

                  <tr>
                    <td class="column-title">
                      {{ $t('@reducePremiumForm/monthlyPriceRowPrefixText') }}
                    </td>
                    <td>{{ contract.periodCost }} kr</td>
                    <td>{{ selectedVariation.premium }} kr</td>
                    <td v-if="diffCost >= 0">
                      {{
                        $t(
                          '@reducePremiumForm/differenceBetweenCurrentAndUpdatedValuesRowTextLess',
                          {
                            diffValue: diffCost,
                          },
                        )
                      }}
                    </td>
                    <td v-else class="red--text">
                      {{
                        $t(
                          '@reducePremiumForm/differenceBetweenCurrentAndUpdatedValuesRowTextMore',
                          {
                            diffValue: Math.abs(diffCost),
                          },
                        )
                      }}
                    </td>
                  </tr>

                  <tr v-if="newInvoiceAmount">
                    <td class="column-title">
                      {{ $t('@reducePremiumForm/newInitialCostHeader') }}
                    </td>
                    <td>{{ oldInvoiceAmount }} kr</td>
                    <td>{{ newInvoiceAmount }} kr</td>
                    <td v-if="newInvoiceAmount - oldInvoiceAmount <= 0">
                      {{
                        $t(
                          '@reducePremiumForm/differenceBetweenCurrentAndUpdatedValuesRowTextLess',
                          {
                            diffValue: newInvoiceAmount - oldInvoiceAmount,
                          },
                        )
                      }}
                    </td>
                    <td v-else class="red--text">
                      {{
                        $t(
                          '@reducePremiumForm/differenceBetweenCurrentAndUpdatedValuesRowTextMore',
                          {
                            diffValue: Math.abs(newInvoiceAmount - oldInvoiceAmount),
                          },
                        )
                      }}
                    </td>
                  </tr>

                  <tr v-if="newPremiumStartDate">
                    <td class="column-title" colspan="3">
                      {{ $t('@reducePremiumForm/newStartDateHeader') }}
                    </td>
                    <td>
                      {{ formatDate(newPremiumStartDate) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" flat="flat" @click="reset">
            {{ $t('@reducePremiumForm/cancelButtonLabel') }}
          </v-btn>
          <v-btn color="green darken-1" flat="flat" @click="submit" :disabled="isDisabled">
            {{ $t('@reducePremiumForm/submitButtonLabel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import ErrorsList from '../../components/ErrorsList';
import { getErrorMessage } from '../../errorHandlers';
import { isoDateFormatter } from '../../utils';

export default {
  components: { ErrorsList },
  props: {
    contract: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      errorMessage: '',
      variations: [],
      selectedCoverage: this.contract.productVariation
        ? this.contract.productVariation.coveredAmount
        : 0,
      newPremiumStartDate: null,
      newInvoiceAmount: null,
      oldInvoiceAmount: null,
    };
  },

  computed: {
    diffCost() {
      return this.selectedVariation ? this.contract.periodCost - this.selectedVariation.premium : 0;
    },
    diffPremium() {
      return this.selectedVariation
        ? this.contract.productVariation.coveredAmount - this.selectedVariation.coveredAmount
        : 0;
    },
    variationsWithoutCurrent() {
      return this.variations.filter(
        (v) =>
          parseInt(v.covered_amount, 10) !=
          parseInt(this.contract.productVariation.coveredAmount, 10),
      );
    },
    selectedVariation() {
      const variation = this.variations.find((v) => v.covered_amount === this.selectedCoverage);
      return (
        variation && {
          id: variation.id,
          coveredAmount: variation.covered_amount,
          premium: variation.premium,
        }
      );
    },
    isDisabled() {
      return (
        this.isLoading ||
        !this.selectedVariation ||
        (this.contract.productVariation &&
          this.selectedVariation.id === this.contract.productVariation.id)
      );
    },
  },

  methods: {
    reset() {
      this.errorMessage = '';
      this.selectedCoverage = this.contract.productVariation
        ? this.contract.productVariation.coveredAmount
        : 0;
      this.$emit('dismiss');
    },

    async fetchProducts() {
      try {
        this.isLoading = true;
        const { data: products = [] } = await this.$http.get('/api/user/products');
        const product = products.find((product) => product.id === this.contract.product.id);

        if (product) {
          this.variations = product.variations;
        } else {
          this.errorMessage = this.$t('@reducePremiumForm/noProductsAvailableErrorMessage');
          return;
        }
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.isLoading = false;
      }
    },

    async submit() {
      try {
        this.isLoading = true;

        if (!this.selectedVariation) {
          this.errorMessage = this.$t('@reducePremiumForm/emptyVariationErrorMessage');
          return;
        }
        await this.$http.put(
          `/api/insurances/${this.contract.id}/variations/${this.selectedVariation.id}`,
        );

        this.$emit('submitted');
        this.reset();
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.isLoading = false;
      }
    },

    async getContractProductVariationInformation() {
      if (!this.selectedVariation) {
        return;
      }

      try {
        this.isLoading = true;
        const { data: information } = await this.$http.get(
          `/api/insurances/${this.contract.id}/variations/${this.selectedVariation.id}`,
        );

        this.newPremiumStartDate = information.newPremiumStartDate;
        this.newInvoiceAmount = information.newInvoiceAmount;
        this.oldInvoiceAmount = information.oldInvoiceAmount;
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.isLoading = false;
      }
    },

    async changeCoverage(coveredAmount) {
      if (coveredAmount !== this.selectedCoverage) {
        this.selectedCoverage = coveredAmount;
        await this.fetchProducts();
      }
    },

    formatDate(date) {
      return isoDateFormatter(date);
    },
  },

  created() {
    this.fetchProducts();
  },
  watch: {
    contract(n) {
      if (n && n.productVariation && n.productVariation.coveredAmount) {
        this.changeCoverage(n.productVariation.coveredAmount);
      }
    },

    selectedVariation(newVariation, oldVariation) {
      if (newVariation && oldVariation && newVariation != oldVariation) {
        this.getContractProductVariationInformation();
      }
    },
  },
};
</script>

<style scoped>
table.v-table tbody td.column-title {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.54) !important;
}
</style>
